/**
 * El mensaje con el que se comparte un presupuesto por WhatsApp (P3/P8).
 *
 * Vive acá porque se comparte desde **dos** lugares —la ficha del presupuesto y el panel de contexto
 * del chat— y el texto tiene que ser el mismo en los dos. Cuando estaba duplicado, cambiarlo en uno
 * dejaba el otro viejo: es justo lo que pasó con `"Acá está el link al presupuesto: …"`.
 */
import { LINK_MARKETPLACE_HASH } from "@/services/XCONS_ENV";

export interface ShareableQuote {
  /** Número que ve el cliente, el mismo de la ficha. */
  quoteId: number;
  /** Nombre del store (`store.name`), no el code: el mensaje lo lee un cliente. */
  storeName?: string | null;
  storeLink?: string | null;
  shareHash?: string | null;
}

/** Link público del presupuesto, o `null` si falta el link del store o el hash. */
export function quoteShareLink(q: ShareableQuote): string | null {
  if (!q.storeLink || !q.shareHash) return null;
  return `${q.storeLink}${LINK_MARKETPLACE_HASH}${q.shareHash}`;
}

/**
 * Texto a enviar. `null` si no se puede armar el link — sin link el mensaje no sirve de nada, así
 * que quien llama avisa el error en lugar de mandar una frase suelta.
 *
 * Si falta el nombre del store se cae el fragmento `en <store>` en vez de escribir "en undefined".
 */
export function quoteShareMessage(q: ShareableQuote): string | null {
  const link = quoteShareLink(q);
  if (!link) return null;
  return quoteLinkMessage(link, { quoteId: q.quoteId, storeName: q.storeName });
}

/**
 * El mismo texto cuando lo que hay es el link ya armado: el que el asistente deja en su nota. Sin el
 * número se cae el `#N`, igual que el `en <store>` sin nombre.
 */
export function quoteLinkMessage(link: string, q: { quoteId?: number | null; storeName?: string | null } = {}): string {
  const store = q.storeName?.trim();
  const where = store ? ` en ${store}` : "";
  const numero = q.quoteId ? ` #${q.quoteId}` : "";
  return `Hola, Te comparto el presupuesto${numero}${where} con los precios siempre actualizados. ${link}`;
}
